/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'

import { cn } from 'app/lib/utils'
import { DialogA11y, useDialogA11ySlot } from 'app/components/ui/dialog-a11y'

/**
 * shadcn-style Sheet — a side-anchored Radix Dialog.
 * Wraps Radix Dialog primitives with the same API shape as shadcn's reference Sheet
 * (Trigger / Content / Header / Title / Description). Side defaults to `right` for
 * the task detail drawer use case.
 *
 * `modal={false}` by default: Radix's modal mode applies `pointer-events: none` to
 * `document.body` and only re-enables them inside the dialog content. That breaks
 * Headless UI modals (e.g. `CallInitiationModal`) opened on top of the drawer —
 * visually they layer correctly at higher z-index but every click is swallowed by
 * Radix's body lock. With `modal={false}` we keep DismissableLayer behavior
 * (Esc / outside-click still close the drawer) without the global pointer lock,
 * so anything portaled above is interactive again.
 */
function Sheet({ modal = false, ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="sheet" modal={modal} {...props} />
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        `data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-30 bg-overlay/40`,
        className,
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = `right`,
  onPointerDownOutside,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  side?: `top` | `right` | `bottom` | `left`
}) {
  // With `modal={false}` on the Root, Radix's DismissableLayer still fires
  // outside-click handlers for any pointerdown outside this content. We
  // suppress that auto-close in two cases:
  //   1. The click is inside another overlay layered above us (a Headless UI
  //      modal with role="dialog" or our legacy DrawerV2 with
  //      x-component-name="Drawer") — closing the overlay shouldn't take us
  //      with it. Lets the user dismiss a click-to-call modal or a contact
  //      profile drawer without losing the underlying task drawer.
  //   2. ANY DrawerV2 is currently open anywhere in the DOM — even if the
  //      click is on the backdrop. This preserves the "contact drawer closes
  //      on outside click, task drawer stays" behavior the user expects.
  //   3. The click is on the alert row, which floats over the page rather than
  //      sitting in it. Dismissing a card is a press on the row, not on us.
  const shouldSuppressClose = (target: Element | null): boolean => {
    if (target?.closest(`[role="dialog"]`)) return true
    if (target?.closest(`[x-component-name="Drawer"]`)) return true
    if (typeof document !== `undefined` && document.querySelector(`[x-component-name="Drawer"]`)) return true
    if (target?.closest(`[data-floating-layer]`)) return true
    return false
  }
  const handlePointerDownOutside: React.ComponentProps<typeof DialogPrimitive.Content>[`onPointerDownOutside`] = (
    e,
  ) => {
    if (shouldSuppressClose(e.target as Element | null)) {
      e.preventDefault()
      return
    }
    onPointerDownOutside?.(e)
  }
  const handleInteractOutside: React.ComponentProps<typeof DialogPrimitive.Content>[`onInteractOutside`] = (e) => {
    if (shouldSuppressClose(e.target as Element | null)) {
      e.preventDefault()
      return
    }
    onInteractOutside?.(e)
  }
  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        data-slot="sheet-content"
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={handleInteractOutside}
        className={cn(
          // `overflow-y-auto` on the Sheet itself means the entire drawer
          // scrolls when its content exceeds viewport height (the previous
          // `flex flex-col` + inner pane overflow strategy didn't survive
          // all flex-item min-height defaults). Themed thin scrollbar.
          `bg-surface-subtle data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-30 overflow-y-auto shadow-elevation-8 transition ease-in-out data-[state=closed]:duration-200 data-[state=open]:duration-300 [scrollbar-color:rgb(212_212_212)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-trn-300 [&::-webkit-scrollbar-thumb:hover]:bg-trn-400`,
          side === `right` &&
            `data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l border-default sm:max-w-2xl`,
          side === `left` &&
            `data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r border-default sm:max-w-sm`,
          side === `top` &&
            `data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b border-default`,
          side === `bottom` &&
            `data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t border-default`,
          className,
        )}
        {...props}
      >
        <DialogA11y>{children}</DialogA11y>
        {/* Callers render their own close affordance inline with their action
            buttons (see TaskDetailPane → SheetClose next to the edit pencil).
            We intentionally don't render a built-in absolute X here because it
            collides with content placed at the drawer's top-right. */}
      </DialogPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-header" className={cn(`flex flex-col gap-1.5 p-6`, className)} {...props} />
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-footer" className={cn(`mt-auto flex flex-col gap-2 p-6`, className)} {...props} />
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  useDialogA11ySlot(`title`)
  return (
    <DialogPrimitive.Title
      data-slot="sheet-title"
      className={cn(`text-heading-md text-strong`, className)}
      {...props}
    />
  )
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  useDialogA11ySlot(`description`)
  return (
    <DialogPrimitive.Description
      data-slot="sheet-description"
      className={cn(`text-body-sm text-subtle`, className)}
      {...props}
    />
  )
}

export { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger }
